import api from './api';

const userService = {
  // Get all users (admin only)
  getAllUsers: async () => {
    try {
      const response = await api.get('/api/users');

      // Handle different API response formats
      let userData;
      if (response.data && response.data.data) {
        // Standard API format: { success: true, data: [...] }
        userData = response.data.data;
      } else if (Array.isArray(response.data)) {
        // API returns array directly
        userData = response.data;
      } else {
        // Fallback
        userData = [];
      }

      return userData;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch users';
    }
  },
  
  // Get a single user by ID (admin only)
  getUserById: async (userId) => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch user';
    }
  },
  
  // Create a new user (admin only)
  createUser: async (userData) => {
    try {
      const response = await api.post('/users', userData);

      // Check if the response indicates failure
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to create user');
      }

      return response.data.data;
    } catch (error) {
      // Handle error response from backend
      // FastAPI returns errors in 'detail' field, our custom API uses 'message'
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to create user';
      throw new Error(errorMessage);
    }
  },
  
  // Update a user (admin only)
  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/users/${userId}`, userData);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update user';
    }
  },
  
  // Activate or deactivate a user (admin only)
  updateUserStatus: async (userId, status) => {
    try {
      const response = await api.patch(`/users/${userId}/status`, { status });
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to update user status');
      }
      return response.data.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to update user status';
      throw new Error(errorMessage);
    }
  },

  // Delete a user (admin only)
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete user';
    }
  },

  // Update current user's t-shirt size
  updateTShirtSize: async (size) => {
    try {
      const response = await api.put('/users/me/tshirt-size', { tShirtSize: size });
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to update t-shirt size');
      }
      return response.data.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to update t-shirt size';
      throw new Error(errorMessage);
    }
  },

  // Get all users' t-shirt sizes (admin only)
  getTShirtSizes: async () => {
    try {
      const response = await api.get('/users/admin/tshirt-sizes');
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to fetch t-shirt sizes');
      }
      return response.data.data || [];
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch t-shirt sizes';
      throw new Error(errorMessage);
    }
  },

  // Export t-shirt sizes as CSV (admin only)
  exportTShirtSizes: async () => {
    try {
      const response = await api.get('/users/admin/tshirt-sizes/export', {
        responseType: 'blob',
      });

      // Create a download link and trigger download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tshirt_sizes.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to export t-shirt sizes';
      throw new Error(errorMessage);
    }
  }
};

export default userService;